/**
 * O sorteio do Dart, refeito aqui número por número.
 *
 * Numa partida 1v1 os dois lados sorteiam as dez perguntas sozinhos, cada um
 * no seu aparelho, a partir da mesma semente — o identificador da partida. Se
 * o `Random` daqui não devolver exatamente a mesma sequência do `Random` de
 * lá, o celular e o navegador jogam listas diferentes, e o pior é que ninguém
 * vê erro nenhum: o `firstWhere(..., orElse: primeira)` do tabuleiro cobre a
 * divergência mostrando a pergunta errada.
 *
 * Por isso isto não é "um gerador qualquer com semente", e sim o
 * `dart-sdk/lib/_internal/vm/lib/math_patch.dart` copiado: a mistura de
 * Thomas Wang para acomodar a semente, o gerador multiply-with-carry com
 * `A = 0xFFFFDA61`, as quatro voltas de aquecimento e o laço de rejeição do
 * `nextInt`. O `shuffle` é o de `dart-sdk/lib/collection/list.dart`, que é uma
 * variante do Fisher-Yates e não a mais comum — trocar por outra dá outra
 * ordem.
 *
 * BigInt de propósito: `<<` e `|` em JavaScript trabalham em 32 bits e
 * truncariam o estado de 64.
 */

const M64 = (1n << 64n) - 1n;
const M32 = 0xffffffffn;
const A = 0xffffda61n;
const POW2_32 = 1n << 32n;
const POW2_27 = 2 ** 27;
const POW2_53 = 2 ** 53;

/** `_Random._setupSeed`: a mistura de Thomas Wang, em 64 bits com sinal. */
function acomodarSemente(semente: number): bigint {
  // O Dart trabalha com inteiro de 64 bits com sinal e desloca à direita sem
  // sinal (`>>>`), então a conta anda em não-sinalizado e o `~` vira `M64 - n`.
  let n = BigInt(semente) & M64;
  n = (M64 - n + ((n << 21n) & M64)) & M64; // ~n + (n << 21)
  n = n ^ (n >> 24n);
  n = (n * 265n) & M64;
  n = n ^ (n >> 14n);
  n = (n * 21n) & M64;
  n = n ^ (n >> 28n);
  n = (n + ((n << 31n) & M64)) & M64;
  if (n === 0n) n = 0x5a17n;
  return n;
}

/** O `Random(semente)` do Dart. */
export class Acaso {
  private estado: bigint;

  constructor(semente: number) {
    this.estado = acomodarSemente(semente);
    // "Crank a couple of times to distribute the seed bits a bit further."
    for (let i = 0; i < 4; i++) this.avancar();
  }

  private avancar(): void {
    const baixo = this.estado & M32;
    const alto = this.estado >> 32n;
    this.estado = (A * baixo + alto) & M64;
  }

  /** `nextInt(max)`, com o mesmo laço de rejeição — inclusive o atalho de potência de dois. */
  proximo(max: number): number {
    const m = BigInt(max);
    if ((m & -m) === m) {
      this.avancar();
      return Number(this.estado & M32 & (m - 1n));
    }
    let rnd32: bigint;
    let resto: bigint;
    do {
      this.avancar();
      rnd32 = this.estado & M32;
      resto = rnd32 % m;
    } while (rnd32 - resto + m > POW2_32);
    return Number(resto);
  }

  /**
   * `nextDouble()`: os 53 bits de mantissa montados a partir de dois sorteios,
   * como no mesmo arquivo do Dart. Os dois limites são potências de dois, então
   * cada um cai no atalho do `proximo` e a conta é a de lá, número por número.
   */
  proximoDouble(): number {
    return (this.proximo(1 << 26) * POW2_27 + this.proximo(1 << 27)) / POW2_53;
  }
}

/**
 * O `List.shuffle` do Dart. Embaralha no lugar e devolve a mesma lista.
 *
 * Sem `acaso`, cai no `Math.random` — que é o que o estudo sozinho quer.
 */
export function embaralhar<T>(itens: T[], acaso?: Acaso): T[] {
  let n = itens.length;
  while (n > 1) {
    const pos = acaso ? acaso.proximo(n) : Math.floor(Math.random() * n);
    n -= 1;
    const guarda = itens[n];
    itens[n] = itens[pos];
    itens[pos] = guarda;
  }
  return itens;
}

/**
 * `_semear`: o identificador da partida vira número.
 *
 * O `hashCode` das strings do Dart não é estável entre aparelhos, então lá se
 * escreveu esta conta à mão. Aqui ela é copiada pelo mesmo motivo, e porque a
 * semente tem de ser a mesma dos dois lados. A máscara mantém o resultado
 * abaixo de 2^30, o que também faz a variante do dart2js coincidir com a da
 * máquina virtual.
 */
export function semear(texto: string): number {
  let h = 7;
  for (let i = 0; i < texto.length; i++) {
    h = (h * 31 + texto.charCodeAt(i)) & 0x3fffffff;
  }
  return h;
}
